import { DEFAULT_SCRIPT_ID, SCRIPT_URL } from '@marsidev/react-turnstile'
import { NextSeo } from 'next-seo'
import Head from 'next/head'
import Router, { useRouter } from 'next/router'
import Script from 'next/script'
import { useCallback, useEffect, useRef } from 'react'

import { FORM_ERROR } from 'final-form'
import Cookies from 'js-cookie'
import signCsrfToken from 'server/csrf'

import AdblockerDetectModal from 'components/AdblockerDetectModal'
import { AuthLayout, GreatTeamsCopy } from 'components/AuthLayout'
import { SELECTED_TIER_KEY } from 'components/onboardingWelcome'
import RegisterForm from 'components/registrationForm'

import useUser from 'hooks/useUser'

import { api } from 'lib/api/api'
import { RegisterFormValues } from 'lib/forms/validators'
import handleSubmissionError from 'lib/handleSubmissionError'

function Register({ csrfToken }: Readonly<{ csrfToken: string }>) {
  const { query } = useRouter()
  const { mutate } = useUser()
  const errorMessage = useRef<null | HTMLDivElement>(null)

  useEffect(() => {
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    const tier = urlParams.get(SELECTED_TIER_KEY)
    if (tier) {
      localStorage.setItem(SELECTED_TIER_KEY, tier)
    }
  }, [])

  const onSubmit = useCallback(
    async ({ email, first_name, last_name, password, recaptcha, ...other }: RegisterFormValues) => {
      try {
        if (other[FORM_ERROR]) throw other[FORM_ERROR]
        if ((window as any).twttr) (window as any).twttr.conversion.trackPid('o4pmz')
        const { data } = await api.createUser(
          recaptcha,
          query?.login_challenge as string,
          {
            email,
            firstname: first_name,
            lastname: last_name,
            name: `${first_name} ${last_name}`,
            password,
            redirect_to: (query?.redirect_to as string) ?? '/welcome'
          },
          {
            headers: {
              csrf_token: csrfToken
            }
          }
        )
        await mutate(data, { optimisticData: data, populateCache: true, revalidate: false })
        await Router.replace('/resendverify')
      } catch (e: any) {
        // potentially remove cached turnstile data
        Cookies.remove('data')
        localStorage.clear()
        // Scroll user to top to see error message
        const currentRef = errorMessage.current
        if (currentRef) {
          currentRef.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        return handleSubmissionError(e)
      }
    },
    [query, mutate, csrfToken]
  )

  return (
    <>
      <Head>
        <link as='script' href={SCRIPT_URL} rel='prefetch' />
      </Head>
      <AuthLayout {...GreatTeamsCopy}>
        <>
          <NextSeo
            description='Register. MetaMask Developer provides instant and scalable API access for web3 dapp developers.'
            title='Register | MetaMask Developer'
          />
          <div className='relative m-auto max-w-3xl px-6 py-8' data-testid='register-form'>
            <AdblockerDetectModal />
            {/* @ts-expect-error enabled to support strictNullChecks - Please fix this if you can! */}
            <RegisterForm errorRef={errorMessage} onSubmit={onSubmit} />
          </div>
        </>
      </AuthLayout>
      <Script id={DEFAULT_SCRIPT_ID} src={SCRIPT_URL} strategy='afterInteractive' />
    </>
  )
}

// @ts-ignore
Register._noLayout = true

export async function getServerSideProps() {
  return {
    props: { csrfToken: signCsrfToken() }
  }
}

export default Register
